import API_PATHS from '@base/features/api/constants/apiPaths';
import { IApiClient } from '@base/features/api/model/apiClient';
import { getRequiredEnv } from '@base/utils/helpers';
import { deserialize } from 'object-deserializer';
import { Subject } from 'rxjs';
import { challengeDeserializer } from './deserializers';
import { Challenge, ChallengeParams, IChallengeService, ChangeChallengeParams } from './interfaces';

export default class ChallengeService implements IChallengeService {
  onChallengeChanged = new Subject<Challenge | undefined>();
  constructor(private apiClient: IApiClient, private gameId: number) {}

  async getChallenge(id: string): Promise<Challenge> {
    const res = await this.apiClient.authGet(
      {
        json: 1,
        gid: this.gameId,
        chid: id,
      },
      API_PATHS.challenge
    );
    const data = await res.json();
    return deserialize(data, challengeDeserializer);
  }

  async sendMessage(id: string, note: string): Promise<Challenge> {
    const res = await this.apiClient.authGet(
      {
        json: 1,
        gid: this.gameId,
        action: 'note',
        chid: id,
        note: note,
      },
      API_PATHS.challenge
    );
    const data = await res.json();
    return deserialize(data, challengeDeserializer);
  }

  async changeChallengeDate(params: ChangeChallengeParams): Promise<Challenge> {
    const res = await this.apiClient.authGet(
      {
        json: 1,
        gid: this.gameId,
        action: 'chng_time',
        ...params,
      },
      API_PATHS.challenge
    );
    const data = await res.json();
    return deserialize(data, challengeDeserializer);
  }

  async sendChallengeRequest(params: ChallengeParams): Promise<Challenge | void> {
    try {
      const res = await this.apiClient.authGet(
        {
          ...params,
          action: 'issue',
          gid: this.gameId,
          room: `${getRequiredEnv('NX_SOCKET_HOST')}:${getRequiredEnv('NX_SOCKET_PORT')}`,
          json: 1,
        },
        API_PATHS.challenge
      );
      const data = await res.json();
      const createdChallenge = deserialize(data, challengeDeserializer);
      this.onChallengeChanged.next(createdChallenge);
      return createdChallenge;
    } catch (e) {
      console.error(e);
      throw new Error(e);
    }
  }

  async withdrawChallenge(challengeId: string): Promise<void> {
    await this.apiClient.authGet(
      {
        json: 1,
        gid: this.gameId,
        chid: challengeId,
        action: 'withdraw',
      },
      API_PATHS.challenge
    );
    this.onChallengeChanged.next(undefined);
  }

  async getAllChallenges(): Promise<Challenge[]> {
    const res = await this.apiClient.authGet(
      {
        json: 1,
        gid: this.gameId,
      },
      API_PATHS.challenge
    );
    const data = await res.json();
    return deserialize(data, (o) => o.required('challenges').asArrayOfObjects(challengeDeserializer));
  }
}
